import { getValueOrDefault } from '../../components/values/Get';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { isCommercialUser, isPrivateUser } from '../../helpers/topContext';
import { IIsCustomerAllowedToSelectWorkshopInput } from '../../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import { IIsCustomerClaimCoveredInput } from '../../helpers/business-rules/isCustomerClaimCovered';
import { formHasElementId } from '../../helpers/formHasElementId';

/**
 * Interface describing which of the checkboxes on the 'hvad-er-skadet' page that was selected.
 */
export interface IWhatHasBeenDamaged {
    vehicle: {
        isMyVehicleDamaged: boolean;
        isOthersVehicleDamaged: boolean;
    };
    persons: {
        isDriverDamaged: boolean;
        isPassangersDamaged: boolean;
    };
    things: {
        isMyThingsDamaged: boolean;
        isOthersThingsDamaged: boolean;
    };
}

/**
 * Returns true if the customer has an existing vehicle policy, else false.
 * @param formConfig
 * @param formValues
 * @returns
 */
export const isCustomerWithExistingVehiclePolicy = (formConfig: FormConfig, formValues: FormValues): boolean => {
    return getValueOrDefault(formConfig, formValues, 'registrationNumber').hasVehiclesInSystem === 'yes';
};

/**
 * Returns on object representing which of the "What has been damaged" checkboxes that are selected.
 * @param formConfig
 * @param formValues
 * @returns
 */
export const getWhatHasBeenDamaged = (formConfig: FormConfig, formValues: FormValues): IWhatHasBeenDamaged => {
    const vehiclesCheckbox = getValueOrDefault(formConfig, formValues, 'whichVehiclesWereDamaged');
    const personsCheckbox = getValueOrDefault(formConfig, formValues, 'whichPersonsWereInjured');
    const thingsCheckbox = getValueOrDefault(formConfig, formValues, 'whichOtherStuffWasDamaged');

    // the 'paakoert-bagfra-privat' form uses two different sets of damage checkboxes,
    // depending on some previous choices in the flow
    let vehiclesCheckbox1 = null;
    let personsCheckbox1 = null;
    let thingsCheckbox1 = null;
    if (formConfig.formId === 'paakoert-bagfra-privat') {
        vehiclesCheckbox1 = getValueOrDefault(formConfig, formValues, 'whichVehiclesWereDamaged1');
        personsCheckbox1 = getValueOrDefault(formConfig, formValues, 'whichPersonsWereInjured1');
        thingsCheckbox1 = getValueOrDefault(formConfig, formValues, 'whichOtherStuffWasDamaged1');
    }

    return {
        vehicle: {
            isMyVehicleDamaged:
                (vehiclesCheckbox &&
                    vehiclesCheckbox.some((element: any, index: number) => element.value === 'myVehicle')) ||
                (vehiclesCheckbox1 &&
                    vehiclesCheckbox1.some((element: any, index: number) => element.value === 'myVehicle')),
            isOthersVehicleDamaged:
                (vehiclesCheckbox &&
                    vehiclesCheckbox.some((element: any, index: number) => element.value === 'othersVehicles')) ||
                (vehiclesCheckbox1 &&
                    vehiclesCheckbox1.some((element: any, index: number) => element.value === 'othersVehicles'))
        },
        persons: {
            isDriverDamaged:
                (personsCheckbox &&
                    personsCheckbox.some((element: any, index: number) => element.value === 'theDriverOfMyVehicle')) ||
                (personsCheckbox1 &&
                    personsCheckbox1.some((element: any, index: number) => element.value === 'theDriverOfMyVehicle')),
            isPassangersDamaged:
                (personsCheckbox &&
                    personsCheckbox.some(
                        (element: any, index: number) => element.value === 'oneOrMorePassengersInMyVehicle'
                    )) ||
                (personsCheckbox1 &&
                    personsCheckbox1.some(
                        (element: any, index: number) => element.value === 'oneOrMorePassengersInMyVehicle'
                    ))
        },
        things: {
            isMyThingsDamaged:
                (thingsCheckbox &&
                    thingsCheckbox.some((element: any, index: number) => element.value === 'myThings')) ||
                (thingsCheckbox1 &&
                    thingsCheckbox1.some((element: any, index: number) => element.value === 'myThings')),
            isOthersThingsDamaged:
                (thingsCheckbox &&
                    thingsCheckbox.some(
                        (element: any, index: number) => element.value === 'othersThingsBuildingsOrAnimals'
                    )) ||
                (thingsCheckbox1 &&
                    thingsCheckbox1.some(
                        (element: any, index: number) => element.value === 'othersThingsBuildingsOrAnimals'
                    ))
        }
    };
};

/**
 * Returns an object containing all the details required to determine if customer should be asked to select a workshop or not.
 * @param formConfig
 * @param formValues
 */
export const getWorkshopDetails = (
    formConfig: FormConfig,
    formValues: FormValues
): IIsCustomerAllowedToSelectWorkshopInput => {
    let vehicleType = '';
    let hasLicenseplate = false;

    if (formHasElementId(formConfig, 'registrationNumber')) {
        if (isCustomerWithExistingVehiclePolicy(formConfig, formValues)) {
            // a customer with an existing policy either selects one of thoose vehicles...
            vehicleType = getValueOrDefault(formConfig, formValues, 'registrationNumber').entityType;
            hasLicenseplate = getValueOrDefault(formConfig, formValues, 'registrationNumber').registrationNumber !== '';

            //...or selects 'others' and specifies the vehicle type and keys in a licenseplate when applicable
            if (vehicleType === 'others') {
                vehicleType = getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value;
                hasLicenseplate =
                    getValueOrDefault(formConfig, formValues, 'doesTheVehicleHaveALicensePlate').value === 'Ja' &&
                    getValueOrDefault(formConfig, formValues, 'licensePlateOther') !== '';
            }
        } else {
            // a no-policy customer selects a vehicle type (or selects 'others' option) and keys in a licenseplate when applicable
            vehicleType = getValueOrDefault(formConfig, formValues, 'registrationNumber').entityType;
            hasLicenseplate =
                getValueOrDefault(formConfig, formValues, 'doesTheVehicleHaveALicensePlate').value === 'Ja' &&
                getValueOrDefault(formConfig, formValues, 'licensePlateOther') !== '';
        }
    } else {
        vehicleType = getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value;
        hasLicenseplate =
            getValueOrDefault(formConfig, formValues, 'doesTheVehicleHaveALicensePlate').value === 'Ja' &&
            getValueOrDefault(formConfig, formValues, 'licensePlateOther') !== '';
    }

    return {
        isPrivateCustomer: isPrivateUser(),
        isCommercialUser: formConfig.formId.endsWith('erhverv-www') ? true : isCommercialUser(),
        vehicleType: vehicleType,
        hasLicenseplate: hasLicenseplate,
        isDamangeOnCustomersVehicle:
            // a parking damage is by definition a damage to the customers vehicle
            // (the parking damage form does not even contain a damage page where the type of damage can be specified)
            formConfig.formId === 'parkeringsskade-privat' ||
            formConfig.formId === 'parkeringsskade-erhverv' ||
            formConfig.formId === 'parkeringsskade-erhverv-www' ||
            formConfig.formId === 'haervaerk-koeretoej-privat'
                ? true
                : getWhatHasBeenDamaged(formConfig, formValues).vehicle.isMyVehicleDamaged,
        dateOfLoss: {
            dateOfLoss: getValueOrDefault(formConfig, formValues, 'dateOfLoss'),
            dateToday: new Date()
        }
    };
};

/**
 * Returns an object containing all the details required to determine if a claim is covered or not.
 * @param formConfig
 * @param formValues
 * @returns
 */
export const getClaimDetails = (formConfig: FormConfig, formValues: FormValues): IIsCustomerClaimCoveredInput => {
    // the 'wasTheDriverIntoxicated' element is present on ALL forms apart from 'parkeringsskade-privat'
    let isDriverIntoxicated = false;
    if (formConfig.formId !== 'parkeringsskade-privat') {
        // if user aswers 'Ja' (yes) or 'Det ved jeg ikke' (I don't know) we interpret both anwers as a yes in regards to the coverage
        isDriverIntoxicated =
            getValueOrDefault(formConfig, formValues, 'wasTheDriverIntoxicated').value === 'no' ? false : true;
    }

    // the 'whichStatementMostAccuratelyDescribesTheSituation' element is ONLY present on the 'ramt-et-koeretoej-privat' form
    let damageSituation = '';
    if (formConfig.formId === 'ramt-et-koeretoej-privat') {
        damageSituation = getValueOrDefault(
            formConfig,
            formValues,
            'whichStatementMostAccuratelyDescribesTheSituation'
        ).value;
    }

    return {
        isPrivateCustomer: true,
        isDriverIntoxicated: isDriverIntoxicated,
        policyNumber: getValueOrDefault(formConfig, formValues, 'registrationNumber').policyNrOrOthers,
        coverages: getValueOrDefault(formConfig, formValues, 'registrationNumber').coverage,
        damageSituation: damageSituation,
        formId: formConfig.formId,

        dateOfLoss: {
            dateOfLoss: getValueOrDefault(formConfig, formValues, 'dateOfLoss'),
            dateToday: new Date()
        }
    };
};
